import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  PlusIcon,
  EyeIcon,
  TruckIcon,
  CheckIcon,
  CheckCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  useSalesOrdersPage,
  useSalesOrderSummary,
  useCreateSalesOrder,
  useUpdateSalesOrderStatus,
} from "../../hooks/useSales";
import {
  SalesOrder,
  SalesOrderStatus,
  CreateSalesOrderRequest,
} from "../../types/sales.types";
import SalesOrderForm from "../../components/forms/SalesOrderForm";
import { Pagination } from "../../components/ui/Pagination";
import { Modal } from "../../components/ui/Modal";
import {
  ConfirmDialog,
  DetailCard,
  DetailItem,
  FormActions,
  badgeClass,
  getApiErrorMessage,
  secondaryButtonClass,
} from "../../components/ui/FormControls";
import { defaultRowsPerPage } from '../../lib/preferences';
import { PlantTag } from '../../components/common/PlantField';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_META: Record<string, { label: string; color: string }> = {
  [SalesOrderStatus.QUOTE]: { label: "Quote", color: "text-gray-700 bg-gray-50 ring-gray-500/20" },
  [SalesOrderStatus.ORDER]: { label: "Order", color: "text-blue-700 bg-blue-50 ring-blue-600/20" },
  [SalesOrderStatus.CONFIRMED]: { label: "Confirmed", color: "text-indigo-700 bg-indigo-50 ring-indigo-600/20" },
  [SalesOrderStatus.PARTIALLY_SHIPPED]: { label: "Partly shipped", color: "text-amber-800 bg-amber-50 ring-amber-600/20" },
  [SalesOrderStatus.SHIPPED]: { label: "Shipped", color: "text-yellow-800 bg-yellow-50 ring-yellow-600/20" },
  [SalesOrderStatus.DELIVERED]: { label: "Delivered", color: "text-green-700 bg-green-50 ring-green-600/20" },
  [SalesOrderStatus.COMPLETED]: { label: "Completed", color: "text-green-700 bg-green-50 ring-green-600/20" },
  [SalesOrderStatus.CANCELLED]: { label: "Cancelled", color: "text-red-700 bg-red-50 ring-red-600/20" },
};

const FALLBACK_BADGE = "text-gray-700 bg-gray-50 ring-gray-500/20";

type StatusAction = {
  status: SalesOrderStatus;
  label: string;
  icon: React.ElementType;
  buttonClass: string;
  title: string;
  message: string;
  success: string;
  tone: "primary" | "danger";
};

// The next step in the order's life, mirroring the transitions the API accepts
const NEXT_ACTION: Partial<Record<SalesOrderStatus, StatusAction>> = {
  [SalesOrderStatus.QUOTE]: {
    status: SalesOrderStatus.ORDER,
    label: "Convert to Order",
    icon: CheckIcon,
    buttonClass: "bg-blue-50 text-blue-700 hover:bg-blue-100",
    title: "Convert Quote to Order",
    message: "The quote becomes a sales order awaiting confirmation.",
    success: "Quote converted to order",
    tone: "primary",
  },
  [SalesOrderStatus.ORDER]: {
    status: SalesOrderStatus.CONFIRMED,
    label: "Confirm",
    icon: CheckCircleIcon,
    buttonClass: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
    title: "Confirm Sales Order",
    message: "The order is confirmed and its stock is held for it. The stock leaves when the goods are dispatched.",
    success: "Sales order confirmed",
    tone: "primary",
  },
  [SalesOrderStatus.CONFIRMED]: {
    status: SalesOrderStatus.SHIPPED,
    label: "Mark Shipped",
    icon: TruckIcon,
    buttonClass: "bg-yellow-50 text-yellow-800 hover:bg-yellow-100",
    title: "Mark as Shipped",
    message: "Everything still to go is dispatched in full and leaves stock. For a part dispatch, use Dispatch instead. A shipped order can no longer be cancelled.",
    success: "Sales order marked as shipped",
    tone: "primary",
  },
  [SalesOrderStatus.PARTIALLY_SHIPPED]: {
    status: SalesOrderStatus.SHIPPED,
    label: "Ship the Rest",
    icon: TruckIcon,
    buttonClass: "bg-yellow-50 text-yellow-800 hover:bg-yellow-100",
    title: "Ship the Rest",
    message: "Everything still to go on this order is dispatched in full and leaves stock.",
    success: "Sales order marked as shipped",
    tone: "primary",
  },
  [SalesOrderStatus.SHIPPED]: {
    status: SalesOrderStatus.DELIVERED,
    label: "Mark Delivered",
    icon: CheckCircleIcon,
    buttonClass: "bg-green-50 text-green-700 hover:bg-green-100",
    title: "Mark as Delivered",
    message: "The order will be closed as delivered.",
    success: "Sales order marked as delivered",
    tone: "primary",
  },
};

const CANCELLABLE = [SalesOrderStatus.QUOTE, SalesOrderStatus.ORDER, SalesOrderStatus.CONFIRMED];
const OPEN_STATUSES = [SalesOrderStatus.ORDER, SalesOrderStatus.CONFIRMED, SalesOrderStatus.PARTIALLY_SHIPPED, SalesOrderStatus.SHIPPED];

const cancelAction = (order: SalesOrder): StatusAction => ({
  status: SalesOrderStatus.CANCELLED,
  label: "Cancel",
  icon: XMarkIcon,
  buttonClass: "bg-red-50 text-red-700 hover:bg-red-100",
  title: "Cancel Sales Order",
  message:
    order.status === SalesOrderStatus.CONFIRMED
      ? "The order will be cancelled and the stock held for it is free again. This cannot be undone."
      : "The order will be cancelled. This cannot be undone.",
  success: "Sales order cancelled",
  tone: "danger",
});

const formatNumber = (value: number) => Number(value || 0).toLocaleString("en-IN");

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(Number(amount || 0));

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

const isOverdue = (order: SalesOrder) => {
  if (!OPEN_STATUSES.includes(order.status)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(order.expectedDeliveryDate) < today;
};

const SalesList: React.FC = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<SalesOrderStatus | "">("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState<SalesOrder | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ order: SalesOrder; action: StatusAction } | null>(null);

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: ordersPage, isLoading, isFetching, error } = useSalesOrdersPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
  });
  const { data: summary, isLoading: isSummaryLoading } = useSalesOrderSummary();
  const createMutation = useCreateSalesOrder();
  const updateStatusMutation = useUpdateSalesOrderStatus();

  const orders = ordersPage?.data ?? [];
  const total = ordersPage?.total ?? 0;
  const totalPages = ordersPage?.totalPages ?? 1;

  // Step back when the current page no longer exists
  useEffect(() => {
    if (ordersPage && page > 1 && page > ordersPage.totalPages) {
      setPage(Math.max(1, ordersPage.totalPages));
    }
  }, [ordersPage, page]);

  const handleCreateOrder = async (data: CreateSalesOrderRequest) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success("Sales order created as a quote");
      setIsFormOpen(false);
      setPage(1);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not create the sales order"));
      throw err;
    }
  };

  const confirmStatusUpdate = async () => {
    if (!pendingAction) return;
    const { order, action } = pendingAction;
    try {
      const updated = await updateStatusMutation.mutateAsync({ id: order.id, status: action.status });
      toast.success(action.success);
      setPendingAction(null);
      // Keep the details dialog in step with the change
      if (viewOrder?.id === order.id) {
        setViewOrder(updated);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not update the order status"));
    }
  };

  const openView = (order: SalesOrder) => {
    setViewOrder(order);
    setIsViewOpen(true);
  };

  const hasFilters = !!(search || statusFilter);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const stats = [
    { label: "Total Orders", value: formatNumber(summary?.total ?? 0), icon: ClipboardDocumentListIcon, tone: "text-indigo-600 bg-indigo-50" },
    { label: "Open Orders", value: formatNumber(summary?.open ?? 0), icon: ClockIcon, tone: "text-yellow-600 bg-yellow-50" },
    { label: "Overdue Delivery", value: formatNumber(summary?.overdue ?? 0), icon: ExclamationTriangleIcon, tone: "text-red-600 bg-red-50" },
    { label: "Order Value (excl. quotes, cancelled)", value: formatCurrency(summary?.revenue ?? 0), icon: BanknotesIcon, tone: "text-green-600 bg-green-50" },
  ];

  const selectClass =
    "h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500";
  const thClass = "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500";
  const actionClass = "inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold disabled:opacity-50";

  const renderStatusBadge = (status: SalesOrderStatus) => (
    <span className={`${badgeClass} ${STATUS_META[status]?.color || FALLBACK_BADGE}`}>
      {STATUS_META[status]?.label || status}
    </span>
  );

  const renderActions = (order: SalesOrder) => {
    const next = NEXT_ACTION[order.status];
    const actions = [
      ...(next ? [next] : []),
      ...(CANCELLABLE.includes(order.status) ? [cancelAction(order)] : []),
    ];

    const dispatchable = order.status === SalesOrderStatus.CONFIRMED || order.status === SalesOrderStatus.PARTIALLY_SHIPPED;
    return [
      ...(dispatchable
        ? [
            <Link
              key="dispatch"
              to={`/dispatches?so=${order.id}`}
              className={`${actionClass} bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}
              title="Enter what is leaving: part dispatches, vehicle, batch"
            >
              <TruckIcon className="h-3.5 w-3.5 mr-1" />
              Dispatch
            </Link>,
            <Link
              key="reserve"
              to={`/reservations?so=${order.id}`}
              className={`${actionClass} bg-gray-50 text-gray-700 hover:bg-gray-100`}
              title="Keep the stock for this order, so that nothing else takes it"
            >
              Reserve
            </Link>,
          ]
        : []),
      ...actions.map((action) => (
      <button
        key={action.status}
        onClick={() => setPendingAction({ order, action })}
        disabled={updateStatusMutation.isPending}
        className={`${actionClass} ${action.buttonClass}`}
      >
        <action.icon className="h-3.5 w-3.5 mr-1" />
        {action.label}
      </button>
      )),
    ];
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading sales orders</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Sales Orders</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage sales orders and track their progress
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsFormOpen(true)}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Sales Order
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">
                {isSummaryLoading ? "—" : stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order number or customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as SalesOrderStatus | "");
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              {Object.values(SalesOrderStatus).map((status) => (
                <option key={status} value={status}>
                  {STATUS_META[status]?.label || status}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Sales Orders Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Order</th>
                <th className={thClass}>Customer</th>
                <th className={thClass}>Order Date</th>
                <th className={thClass}>Expected Delivery</th>
                <th className={thClass}>Items</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Amount</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? "opacity-60" : ""}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-sm text-gray-500 text-center" colSpan={8}>
                    Loading sales orders...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={8}>
                    <ClipboardDocumentListIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No sales orders found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? "Try changing or clearing the search and filters."
                        : "Create your first sales order to start selling."}
                    </p>
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const overdue = isOverdue(order);
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                          {order.orderNumber}
                        </span>
                        <PlantTag plantId={order.plantId} className="mt-1" />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{order.customer?.name || "N/A"}</div>
                        {order.customer?.code && <div className="text-gray-500">{order.customer.code}</div>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {formatDate(order.orderDate)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div className={overdue ? "font-medium text-red-600" : "text-gray-700"}>
                          {formatDate(order.expectedDeliveryDate)}
                        </div>
                        {overdue && <div className="text-xs text-red-600">Overdue</div>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {order.items?.length ?? 0}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">{renderStatusBadge(order.status)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-right font-medium text-gray-900 tabular-nums">
                        {formatCurrency(order.grandTotal)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1">
                          {renderActions(order)}
                          <button
                            onClick={() => openView(order)}
                            className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded hover:bg-indigo-50"
                            title="View Details"
                            aria-label="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} orders
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination
              className="lg:flex-1"
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Create Sales Order */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => !createMutation.isPending && setIsFormOpen(false)}
        title="Create Sales Order"
        description="New orders start as a quote and are converted and confirmed from the list."
        size="2xl"
        className="overflow-visible"
      >
        <SalesOrderForm
          onSubmit={handleCreateOrder}
          onCancel={() => setIsFormOpen(false)}
          isLoading={createMutation.isPending}
        />
      </Modal>

      {/* Sales Order Details */}
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Sales Order Details"
        size="xl"
      >
        {viewOrder && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p>
                  <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                    {viewOrder.orderNumber}
                  </span>
                </p>
                <h4 className="mt-2 text-lg font-semibold text-gray-900">
                  {viewOrder.customer?.name || "N/A"}
                </h4>
                {viewOrder.customer?.code && (
                  <p className="text-sm text-gray-500">{viewOrder.customer.code}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isOverdue(viewOrder) && (
                  <span className={`${badgeClass} text-red-700 bg-red-50 ring-red-600/20`}>Overdue</span>
                )}
                {renderStatusBadge(viewOrder.status)}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <DetailCard title="Order">
                <DetailItem label="Order date">{formatDate(viewOrder.orderDate)}</DetailItem>
                <DetailItem label="Expected delivery">{formatDate(viewOrder.expectedDeliveryDate)}</DetailItem>
                <DetailItem label="Payment terms">{viewOrder.paymentTerms || "—"}</DetailItem>
                <DetailItem label="Delivery terms">{viewOrder.deliveryTerms || "—"}</DetailItem>
              </DetailCard>

              <DetailCard title="Delivery">
                <DetailItem label="Shipping method">{viewOrder.shippingMethod || "—"}</DetailItem>
                <DetailItem label="Delivery location">{viewOrder.deliveryLocation || "—"}</DetailItem>
                <DetailItem label="Promised date">{formatDate(viewOrder.promisedDate)}</DetailItem>
                <DetailItem label="Actual delivery">{formatDate(viewOrder.actualDeliveryDate)}</DetailItem>
              </DetailCard>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Product</th>
                    <th className={`${thClass} text-right`}>Qty</th>
                    <th className={`${thClass} text-right`}>Unit Price</th>
                    <th className={`${thClass} text-right`}>Discount</th>
                    <th className={`${thClass} text-right`}>Tax</th>
                    <th className={`${thClass} text-right`}>Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {(viewOrder.items ?? []).length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-sm text-gray-500 text-center" colSpan={6}>
                        This order has no items
                      </td>
                    </tr>
                  ) : (
                    viewOrder.items.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.productName}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-700 tabular-nums">
                          {formatNumber(item.quantity)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-700 tabular-nums">
                          {formatCurrency(item.unitPrice)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-700 tabular-nums">
                          {formatCurrency(item.discountAmount)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-700 tabular-nums">
                          {formatCurrency(item.taxAmount)}
                          <span className="text-gray-500"> ({Number(item.taxRate || 0)}%)</span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-right font-medium text-gray-900 tabular-nums">
                          {formatCurrency(item.totalPrice)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Shipping address</p>
                  <p className="mt-1 whitespace-pre-wrap text-gray-900">{viewOrder.shippingAddress || "—"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</p>
                  <p className="mt-1 whitespace-pre-wrap text-gray-900">{viewOrder.notes || "—"}</p>
                </div>
                {viewOrder.specialInstructions && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Special instructions
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-gray-900">{viewOrder.specialInstructions}</p>
                  </div>
                )}
              </div>
              <dl className="self-start rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
                <div className="flex justify-between py-1">
                  <dt className="text-gray-600">Subtotal</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(viewOrder.totalAmount)}</dd>
                </div>
                <div className="flex justify-between py-1">
                  <dt className="text-gray-600">Discount</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">
                    -{formatCurrency(viewOrder.discountAmount)}
                  </dd>
                </div>
                <div className="flex justify-between py-1">
                  <dt className="text-gray-600">Tax</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(viewOrder.taxAmount)}</dd>
                </div>
                <div className="mt-2 flex justify-between border-t border-gray-200 pt-3">
                  <dt className="font-semibold text-gray-900">Grand Total</dt>
                  <dd className="text-base font-semibold text-gray-900 tabular-nums">
                    {formatCurrency(viewOrder.grandTotal)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        <FormActions>
          <button type="button" onClick={() => setIsViewOpen(false)} className={secondaryButtonClass}>
            Close
          </button>
          {viewOrder && <div className="flex flex-wrap gap-2">{renderActions(viewOrder)}</div>}
        </FormActions>
      </Modal>

      {/* Status change confirmation */}
      <ConfirmDialog
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmStatusUpdate}
        title={pendingAction?.action.title || ""}
        message={
          <>
            <span className="font-medium text-gray-900">{pendingAction?.order.orderNumber}</span> ·{" "}
            {pendingAction?.order.customer?.name || "N/A"} ·{" "}
            {formatCurrency(pendingAction?.order.grandTotal ?? 0)}
            <span className="mt-1 block">{pendingAction?.action.message}</span>
          </>
        }
        confirmLabel={pendingAction?.action.label || "Confirm"}
        pendingLabel="Updating..."
        isPending={updateStatusMutation.isPending}
        tone={pendingAction?.action.tone || "primary"}
      />
    </div>
  );
};

export default SalesList;
